import { AnchorHTMLAttributes, createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { registerAppNavigate } from './appNavigation'

type NavigateOptions = { replace?: boolean }
export type LocationInfo = { pathname: string; search: string; hash: string }
type RouterValue = {
  pathname: string
  search: string
  hash: string
  navigate: (to: string, options?: NavigateOptions) => void
}
const RouterContext = createContext<RouterValue | null>(null)

function currentLocation(): LocationInfo {
  const path = window.location.pathname.replace(/\/+$/, '')
  return {
    pathname: path || '/',
    search: window.location.search || '',
    hash: window.location.hash || '',
  }
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<LocationInfo>(currentLocation)
  useEffect(() => {
    const onPopState = () => setLoc(currentLocation())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])
  const value = useMemo<RouterValue>(() => ({
    pathname: loc.pathname,
    search: loc.search,
    hash: loc.hash,
    navigate(to, options) {
      const url = new URL(to, window.location.origin)
      if (url.origin !== window.location.origin) throw new Error('Cross-origin navigation is not allowed')
      if (options?.replace) window.history.replaceState(null, '', url.pathname + url.search + url.hash)
      else window.history.pushState(null, '', url.pathname + url.search + url.hash)
      setLoc(currentLocation())
      window.scrollTo({ top: 0, behavior: 'auto' })
    },
  }), [loc])
  // Code above this provider (the notification toast) navigates through here
  // instead of reloading the whole application.
  useEffect(() => registerAppNavigate(value.navigate), [value.navigate])
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

function useRouter() {
  const value = useContext(RouterContext)
  if (!value) throw new Error('RouterProvider is missing')
  return value
}

export function useNavigate() { return useRouter().navigate }
export function useLocation(): LocationInfo {
  const router = useRouter()
  return { pathname: router.pathname, search: router.search, hash: router.hash }
}

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const navigate = useNavigate()
  useEffect(() => { navigate(to, { replace }) }, [navigate, replace, to])
  return null
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }
export function Link({ to, onClick, children, ...props }: LinkProps) {
  const navigate = useNavigate()
  return <a {...props} href={to} onClick={event => {
    onClick?.(event)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || props.target === '_blank') return
    event.preventDefault(); navigate(to)
  }}>{children}</a>
}

type NavState = { isActive: boolean }
type NavLinkProps = Omit<LinkProps, 'className' | 'children'> & {
  end?: boolean
  className?: string | ((state: NavState) => string)
  children?: ReactNode | ((state: NavState) => ReactNode)
}
export function NavLink({ to, end, className, children, ...props }: NavLinkProps) {
  const { pathname } = useLocation()
  const isDashboardMatch = (to === '/' && (pathname === '/' || pathname === '/dashboard'))
  const isActive = end
    ? (pathname === to || isDashboardMatch)
    : (pathname === to || isDashboardMatch || (to !== '/' && pathname.startsWith(to + '/')))
  const state = { isActive }
  return <Link {...props} to={to} aria-current={isActive ? 'page' : undefined} className={typeof className === 'function' ? className(state) : className}>{typeof children === 'function' ? children(state) : children}</Link>
}
